import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Badge, Avatar, Menu, MenuItem, Drawer, List, ListItem, ListItemIcon, ListItemText, Divider, Box, useMediaQuery, useTheme } from '@mui/material';
import { Bell, ShoppingCart, LogOut, User, Menu as MenuIcon, X, LayoutDashboard, Search, Package, FileText, Settings, Users, BarChart3, MapPin, ChevronDown, Building2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { dummyNotifications } from '../../data/dummyData';

const brandNav = [
  { label:'Dashboard', path:'/brand/dashboard', icon:<LayoutDashboard size={16}/> },
  { label:'Search Spaces', path:'/brand/search', icon:<Search size={16}/> },
  { label:'My Orders', path:'/brand/orders', icon:<Package size={16}/> },
  { label:'Campaigns', path:'/brand/campaigns', icon:<MapPin size={16}/> },
  { label:'Invoices', path:'/brand/invoices', icon:<FileText size={16}/> },
];

const adminNav = [
  { label:'Dashboard', path:'/admin/dashboard', icon:<LayoutDashboard size={16}/> },
  { label:'Listings', path:'/admin/listings', icon:<MapPin size={16}/> },
  { label:'Bookings', path:'/admin/bookings', icon:<Package size={16}/> },
  { label:'Providers', path:'/admin/providers', icon:<Users size={16}/> },
  { label:'Brands', path:'/admin/brands', icon:<Building2 size={16}/> }, 
  { label:'Reports', path:'/admin/reports', icon:<BarChart3 size={16}/> },
  { label:'Settings', path:'/admin/settings', icon:<Settings size={16}/> },
];

export const Navbar: React.FC = () => {
  const { user, logout, isAdmin } = useAuth();
  const { itemCount } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [anchorEl, setAnchorEl] = useState<null|HTMLElement>(null);
  const [notifAnchor, setNotifAnchor] = useState<null|HTMLElement>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const navItems = isAdmin ? adminNav : brandNav;
  const unread = dummyNotifications.filter(n => !n.isRead).length;

  return (
    <>
      <nav style={{ position:'fixed', top:0, left:0, right:0, zIndex:1300, background:'rgba(255,255,255,0.95)', backdropFilter:'blur(12px)', borderBottom:'1px solid var(--border)', boxShadow:'0 1px 0 0 var(--border-light)' }}>
        <div style={{ maxWidth:1400, margin:'0 auto', padding:'0 24px', height:64, display:'flex', alignItems:'center', gap:0 }}>

          {/* Logo */}
          <Link to={isAdmin?'/admin/dashboard':'/brand/dashboard'} style={{ textDecoration:'none', display:'flex', alignItems:'center', gap:10, marginRight:40 }}>
            <div style={{ width:34, height:34, borderRadius:10, background:'linear-gradient(135deg, #e8621a, #c2490e)', display:'flex', alignItems:'center', justifyContent:'center', boxShadow:'0 4px 12px rgba(232,98,26,0.3)' }}>
              <span style={{ color:'#fff', fontFamily:'Playfair Display,serif', fontWeight:700, fontSize:18, lineHeight:1 }}>A</span>
            </div>
            <div>
              <span style={{ color:'var(--text-primary)', fontFamily:'Playfair Display,serif', fontWeight:700, fontSize:19, letterSpacing:'-0.02em' }}>adroj</span>
              <span style={{ color:'var(--brand)', fontFamily:'Playfair Display,serif', fontWeight:700 }}>.</span>
              {isAdmin && <span style={{ fontSize:10, background:'var(--brand-mid)', color:'var(--brand)', padding:'1px 6px', borderRadius:4, fontFamily:'DM Sans,sans-serif', fontWeight:600, marginLeft:6, letterSpacing:'0.04em' }}>ADMIN</span>}
            </div>
          </Link>

          {/* Desktop Nav */}
          {!isMobile && (
            <div style={{ display:'flex', gap:2, flex:1 }}>
              {navItems.map(item => {
                const active = location.pathname === item.path;
                return (
                  <Link key={item.path} to={item.path} style={{ textDecoration:'none' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:6, padding:'6px 12px', borderRadius:8, background: active?'var(--brand-light)':'transparent', color: active?'var(--brand)':'var(--text-secondary)', fontFamily:'DM Sans,sans-serif', fontWeight:active?600:500, fontSize:13, cursor:'pointer', transition:'all 0.18s', border: active?'1px solid var(--brand-mid)':'1px solid transparent' }}
                      onMouseEnter={e => { if(!active){ (e.currentTarget as HTMLDivElement).style.background='var(--bg-muted)'; (e.currentTarget as HTMLDivElement).style.color='var(--text-primary)'; }}}
                      onMouseLeave={e => { if(!active){ (e.currentTarget as HTMLDivElement).style.background='transparent'; (e.currentTarget as HTMLDivElement).style.color='var(--text-secondary)'; }}}>
                      {item.icon} {item.label}
                    </div>
                  </Link>
                );
              })}
            </div>
          )}

          <div style={{ marginLeft:'auto', display:'flex', alignItems:'center', gap:6 }}>
            {/* Cart */}
            {!isAdmin && (
              <button onClick={() => navigate('/brand/cart')} style={{ background:'transparent', border:'1px solid var(--border)', borderRadius:10, width:38, height:38, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', color:'var(--text-secondary)', position:'relative', transition:'all 0.18s' }}
                onMouseEnter={e=>{(e.currentTarget).style.borderColor='var(--brand)';(e.currentTarget).style.color='var(--brand)';}}
                onMouseLeave={e=>{(e.currentTarget).style.borderColor='var(--border)';(e.currentTarget).style.color='var(--text-secondary)';}}>
                <ShoppingCart size={17}/>
                {itemCount > 0 && <span style={{ position:'absolute', top:-4, right:-4, background:'var(--brand)', color:'#fff', width:17, height:17, borderRadius:'50%', fontSize:10, fontWeight:700, display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'DM Sans,sans-serif' }}>{itemCount}</span>}
              </button>
            )}

            {/* Notifications */}
            <button onClick={e=>setNotifAnchor(e.currentTarget)} style={{ background:'transparent', border:'1px solid var(--border)', borderRadius:10, width:38, height:38, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', color:'var(--text-secondary)', position:'relative', transition:'all 0.18s' }}
              onMouseEnter={e=>{(e.currentTarget).style.borderColor='var(--brand)';(e.currentTarget).style.color='var(--brand)';}}
              onMouseLeave={e=>{(e.currentTarget).style.borderColor='var(--border)';(e.currentTarget).style.color='var(--text-secondary)';}}>
              <Bell size={17}/>
              {unread > 0 && <span style={{ position:'absolute', top:-4, right:-4, background:'#ef4444', color:'#fff', width:17, height:17, borderRadius:'50%', fontSize:10, fontWeight:700, display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'DM Sans,sans-serif' }}>{unread}</span>}
            </button>

            {/* User */}
            <button onClick={e=>setAnchorEl(e.currentTarget)} style={{ display:'flex', alignItems:'center', gap:8, background:'var(--bg-muted)', border:'1px solid var(--border)', borderRadius:10, padding:'5px 10px 5px 6px', cursor:'pointer', transition:'all 0.18s' }}
              onMouseEnter={e=>(e.currentTarget).style.borderColor='var(--brand)'}
              onMouseLeave={e=>(e.currentTarget).style.borderColor='var(--border)'}>
              <Avatar sx={{ width:28, height:28, bgcolor:'var(--brand)', fontFamily:'DM Sans', fontWeight:700, fontSize:12 }}>{user?.name?.charAt(0)}</Avatar>
              {!isMobile && <span style={{ color:'var(--text-primary)', fontFamily:'DM Sans,sans-serif', fontWeight:500, fontSize:13, maxWidth:120, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{user?.name}</span>}
              <ChevronDown size={13} color="var(--text-muted)"/>
            </button>

            {isMobile && (
              <button onClick={()=>setDrawerOpen(true)} style={{ background:'transparent', border:'1px solid var(--border)', borderRadius:10, width:38, height:38, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', color:'var(--text-secondary)' }}>
                <MenuIcon size={18}/>
              </button>
            )}
          </div>
        </div>
      </nav>

      {/* User Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={()=>setAnchorEl(null)}
        PaperProps={{ sx:{ mt:1, borderRadius:'12px', border:'1px solid var(--border)', boxShadow:'var(--shadow-md)', minWidth:200, '& .MuiList-root':{p:'6px'} } }}>
        <div style={{ padding:'12px 14px 8px', borderBottom:'1px solid var(--border-light)' }}>
          <div style={{ fontFamily:'DM Sans', fontWeight:600, fontSize:13, color:'var(--text-primary)' }}>{user?.name}</div>
          <div style={{ fontFamily:'DM Sans', fontSize:12, color:'var(--text-muted)', marginTop:2 }}>{user?.email}</div>
        </div>
        <MenuItem onClick={()=>{logout();navigate('/login');}} sx={{ gap:1.5, color:'#b91c1c', fontSize:13, borderRadius:'8px', mt:'4px' }}>
          <LogOut size={15}/> Sign Out
        </MenuItem>
      </Menu>

      {/* Notifications */}
      <Menu anchorEl={notifAnchor} open={Boolean(notifAnchor)} onClose={()=>setNotifAnchor(null)}
        PaperProps={{ sx:{ mt:1, borderRadius:'14px', border:'1px solid var(--border)', boxShadow:'var(--shadow-lg)', width:340, maxHeight:420 } }}>
        <div style={{ padding:'14px 16px 10px', borderBottom:'1px solid var(--border-light)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <span style={{ fontFamily:'DM Sans', fontWeight:700, fontSize:14, color:'var(--text-primary)' }}>Notifications</span>
          <span style={{ background:'var(--brand-light)', color:'var(--brand)', padding:'2px 8px', borderRadius:10, fontFamily:'DM Sans', fontSize:11, fontWeight:600 }}>{unread} new</span>
        </div>
        {dummyNotifications.map(n => (
          <MenuItem key={n.id} sx={{ py:1.5, px:2, alignItems:'flex-start', gap:1.5, borderBottom:'1px solid var(--border-light)', whiteSpace:'normal' }}>
            <div style={{ width:8, height:8, borderRadius:'50%', background: n.type==='success'?'var(--success)':n.type==='warning'?'var(--warning)':'var(--info)', marginTop:5, flexShrink:0 }} />
            <div>
              <div style={{ fontFamily:'DM Sans', fontSize:13, fontWeight:n.isRead?400:600, color:'var(--text-primary)' }}>{n.title}</div>
              <div style={{ fontFamily:'DM Sans', fontSize:12, color:'var(--text-muted)', marginTop:2, lineHeight:1.4 }}>{n.message}</div>
            </div>
          </MenuItem>
        ))}
      </Menu>

      {/* Mobile Drawer */}
      <Drawer anchor="right" open={drawerOpen} onClose={()=>setDrawerOpen(false)}
        PaperProps={{ sx:{ width:280, background:'#fff', borderLeft:'1px solid var(--border)' } }}>
        <div style={{ padding:'16px 20px', display:'flex', justifyContent:'space-between', alignItems:'center', borderBottom:'1px solid var(--border)' }}>
          <span style={{ fontFamily:'Playfair Display,serif', fontWeight:700, fontSize:18, color:'var(--text-primary)' }}>adroj<span style={{ color:'var(--brand)' }}>.</span></span>
          <button onClick={()=>setDrawerOpen(false)} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-muted)', padding:4 }}><X size={18}/></button>
        </div>
        <List sx={{ p:1 }}>
          {navItems.map(item => {
            const active = location.pathname === item.path;
            return (
              <ListItem key={item.path} component={Link} to={item.path} onClick={()=>setDrawerOpen(false)}
                sx={{ borderRadius:'10px', mb:'2px', background:active?'var(--brand-light)':'transparent', color:active?'var(--brand)':'var(--text-secondary)', '&:hover':{ background:'var(--bg-muted)', color:'var(--text-primary)' } }}>
                <ListItemIcon sx={{ color:'inherit', minWidth:32 }}>{item.icon}</ListItemIcon>
                <ListItemText primary={item.label} primaryTypographyProps={{ fontFamily:'DM Sans', fontSize:14, fontWeight:active?600:400 }}/>
              </ListItem>
            );
          })}
        </List>
      </Drawer>

      <div style={{ height:64 }} />
    </>
  );
};
